import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getWorkOrders } from 'app/entities/work-order/work-order.reducer';
import { getEntities as getEmployees } from 'app/entities/employee/employee.reducer';
import { TaskStatus } from 'app/shared/model/enumerations/task-status.model';
import { createEntity, getEntity, reset, updateEntity } from './task.reducer';

export const TaskUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const workOrders = useAppSelector(state => state.workOrder.entities);
  const employees = useAppSelector(state => state.employee.entities);
  const taskEntity = useAppSelector(state => state.task.entity);
  const loading = useAppSelector(state => state.task.loading);
  const updating = useAppSelector(state => state.task.updating);
  const updateSuccess = useAppSelector(state => state.task.updateSuccess);
  const taskStatusValues = Object.keys(TaskStatus);

  const handleClose = () => {
    navigate('/task');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getWorkOrders({}));
    dispatch(getEmployees({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    values.createdTimestamp = convertDateTimeToServer(values.createdTimestamp);

    const entity = {
      ...taskEntity,
      ...values,
      workOrder: workOrders.find(it => it.id.toString() === values.workOrder?.toString()),
      owner: employees.find(it => it.id.toString() === values.owner?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdTimestamp: displayDefaultDateTime(),
        }
      : {
          taskStatus: 'NEW',
          ...taskEntity,
          createdTimestamp: convertDateTimeFromServer(taskEntity.createdTimestamp),
          workOrder: taskEntity?.workOrder?.id,
          owner: taskEntity?.owner?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.task.home.createOrEditLabel" data-cy="TaskCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.task.home.createOrEditLabel">Create or edit a Task</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="task-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.task.name')}
                id="task-name"
                name="name"
                data-cy="name"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.task.description')}
                id="task-description"
                name="description"
                data-cy="description"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.task.startDate')}
                id="task-startDate"
                name="startDate"
                data-cy="startDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.task.endDate')}
                id="task-endDate"
                name="endDate"
                data-cy="endDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.task.taskStatus')}
                id="task-taskStatus"
                name="taskStatus"
                data-cy="taskStatus"
                type="select"
              >
                {taskStatusValues.map(taskStatus => (
                  <option value={taskStatus} key={taskStatus}>
                    {translate(`crawlerBackendApp.TaskStatus.${taskStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.task.createdTimestamp')}
                id="task-createdTimestamp"
                name="createdTimestamp"
                data-cy="createdTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="task-workOrder"
                name="workOrder"
                data-cy="workOrder"
                label={translate('crawlerBackendApp.task.workOrder')}
                type="select"
              >
                <option value="" key="0" />
                {workOrders
                  ? workOrders.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="task-owner"
                name="owner"
                data-cy="owner"
                label={translate('crawlerBackendApp.task.owner')}
                type="select"
              >
                <option value="" key="0" />
                {employees
                  ? employees.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/task" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TaskUpdate;
